import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  AlertTriangle,
  ChevronDown,
  Download,
  Loader,
} from "lucide-react";
import ModelIcon from "./dashboard/ModelIcon";

/**
 * One model's answer on the consumer page.
 *
 * Deliberately not the attorney workspace's ResponseCard: that one carries
 * Dashboard.css, a collapsible fixed-height body and workspace-specific
 * controls, none of which belong on a public page. This is the flat card from
 * the consumer design — identity, body, edit/export — plus the streaming and
 * error states the real endpoint can produce, which a static design has no
 * reason to show.
 *
 * There is no edit control: this is a public page and the answer is the
 * model's, not the visitor's. Export is the only thing they take away.
 */
export default function ConsumerResponseCard({
  model,
  html = "",
  loading = false,
  error = "",
  onExport,
}) {
  const hasBody = html.trim() !== "";

  // Collapsing is offered once there is something to collapse. Answers open by
  // default — they are what the visitor asked for — but four of them at once
  // is a lot of page, so any one can be folded away to reach the summary.
  const [open, setOpen] = useState(true);
  const canToggle = hasBody || !!error;

  // The body scrolls inside the card so five answers do not turn the page into
  // a mile of text. That means the newest tokens of a streaming answer would
  // land below the fold, so the box follows the stream — until the visitor
  // scrolls up to read something, at which point it stays where they put it.
  const bodyRef = useRef(null);
  const followRef = useRef(true);
  const [more, setMore] = useState(false);

  const syncScroll = useCallback(() => {
    const el = bodyRef.current;
    if (!el) return;
    const remaining = el.scrollHeight - el.scrollTop - el.clientHeight;
    // A few pixels of slack: fractional scroll heights never settle on exactly 0.
    const atBottom = remaining < 24;
    followRef.current = atBottom;
    setMore(!atBottom);
  }, []);

  useEffect(() => {
    const el = bodyRef.current;
    if (!el) return;
    if (loading && followRef.current) el.scrollTop = el.scrollHeight;
    syncScroll();
  }, [html, loading, syncScroll]);

  // Following the stream leaves the card parked at the end of the answer, so
  // the first thing a visitor saw once it finished was the Disclaimer — the
  // shorter card made that worse, not better. Send it back to the top on the
  // loading -> done edge, which is where the Short Answer is.
  //
  // Only on that edge: re-running whenever `loading` is false would yank the
  // card back under anyone reading it.
  const wasLoadingRef = useRef(loading);
  useEffect(() => {
    const finished = wasLoadingRef.current && !loading;
    wasLoadingRef.current = loading;
    if (!finished) return;

    const el = bodyRef.current;
    if (!el) return;
    el.scrollTop = 0;
    followRef.current = false;
    syncScroll();
  }, [loading, syncScroll]);

  // The models restate the question back before answering it. That is useful
  // confirmation but it is the one part the visitor already knows, so a long
  // question would otherwise push the actual answer out of view. Clamp the
  // restatement to two lines; the whole thing is still selectable and exports
  // in full.
  //
  // Done here rather than in CSS because the section is a paragraph following
  // a bolded heading inside model-generated HTML — there is no selector that
  // picks it out without also catching every other section's first paragraph.
  useEffect(() => {
    const el = bodyRef.current;
    if (!el) return;

    el.querySelectorAll("[data-lq-clamp]").forEach((n) => {
      n.removeAttribute("data-lq-clamp");
    });

    const heading = [...el.querySelectorAll("p, h1, h2, h3, h4")].find((n) => {
      const strong = n.querySelector("strong");
      const text = (strong ? strong.textContent : n.textContent) || "";
      return /your legal question/i.test(text.trim());
    });

    const body = heading && heading.nextElementSibling;
    // Only clamp a plain paragraph — a list or table would clamp badly, and a
    // model that answers in one of those is not padding out the restatement.
    if (body && body.tagName === "P") body.setAttribute("data-lq-clamp", "");
  }, [html]);

  const panelId = `lq-panel-${model}`;

  return (
    <section
      className="lq-response"
      data-model={model}
      data-collapsed={canToggle && !open ? "true" : undefined}
    >
      <header className="lq-response-head">
        <span className="lq-response-badge" aria-hidden="true">
          <ModelIcon model={model} size={16} colored />
        </span>
        <h2 className="lq-response-title">{model}'s Response Analysis</h2>

        <div className="lq-response-actions">
          {loading && (
            <span className="lq-response-status">
              <Loader size={13} className="lq-spin" aria-hidden="true" />
              Analyzing…
            </span>
          )}
          {canToggle && (
            <button
              type="button"
              className="lq-toggle"
              onClick={() => setOpen((v) => !v)}
              aria-expanded={open}
              aria-controls={panelId}
              aria-label={
                open
                  ? `Collapse ${model}'s response`
                  : `Expand ${model}'s response`
              }
              title={open ? "Collapse" : "Expand"}
            >
              <ChevronDown size={17} aria-hidden="true" />
            </button>
          )}
        </div>
      </header>

      <div className="lq-collapse" id={panelId}>
        <div className="lq-collapse-inner">
          {error ? (
            <p className="lq-response-error">
              <AlertTriangle size={15} aria-hidden="true" />
              <span>{error}</span>
            </p>
          ) : hasBody ? (
            <div
              className="lq-response-scroll"
              data-more={more ? "true" : undefined}
            >
              <div
                ref={bodyRef}
                onScroll={syncScroll}
                className="lq-response-body lq-rich"
                tabIndex={0}
                role="region"
                aria-label={`${model}'s response`}
                dangerouslySetInnerHTML={{ __html: html }}
              />
            </div>
          ) : (
            // Streaming has started but no tokens have landed yet.
            <div className="lq-response-skeleton" aria-hidden="true">
              <span />
              <span />
              <span />
            </div>
          )}

          {/* Export only once the answer is complete — exporting mid-stream
              would save half a response. Sits outside the scroll area so it
              stays reachable without scrolling the body to the end. */}
          {!error && hasBody && !loading && (
            <div className="lq-response-foot">
              <button type="button" onClick={onExport} className="lq-ghost-btn">
                <Download size={14} aria-hidden="true" />
                Export
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
